import { ReactFlow, Background, Controls, Handle, Position, BaseEdge, EdgeLabelRenderer, getBezierPath } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { typography, radius } from '@/shared/theme/tokens';
import { CAUSAL_NODE_TYPE } from '../recommendationMeta';

// Extracted from RecommendationDrawer: the causal-chain graph (React Flow layout
// with a topological-sort + barycenter heuristic) and its custom node/edge.

function CausalNode({ data }) {
  const m = CAUSAL_NODE_TYPE[data.nodeType] ?? CAUSAL_NODE_TYPE.monitored;
  return (
    <div style={{
      background: data.isDark ? `${m.text}18` : m.bg,
      border: `1.5px solid ${m.border}`, borderRadius: radius.lg,
      padding: '10px 12px', width: 150, textAlign: 'center',
      fontFamily: typography.fontFamily.body,
    }}>
      <Handle type="target" position={Position.Left} style={{ background: m.border, width: 7, height: 7 }} />
      <p style={{ margin: '0 0 4px', fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: m.text, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.widest }}>
        {m.label}
      </p>
      <p style={{ margin: '0 0 4px', fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold, color: data.textPrimary, lineHeight: 1.3, wordBreak: 'break-word' }}>{data.label}</p>
      <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: data.textSubtle, fontWeight: typography.fontWeight.semibold }}>{data.value}</p>
      <Handle type="source" position={Position.Right} style={{ background: m.border, width: 7, height: 7 }} />
    </div>
  );
}

function CausalEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data }) {
  const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, curvature: data.curvature ?? 0.25 });
  return (
    <>
      <BaseEdge id={id} path={edgePath} style={{ stroke: data.borderColor, strokeWidth: 2 }} markerEnd={data.markerEnd} />
      <EdgeLabelRenderer>
        <div style={{
          position: 'absolute',
          transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
          pointerEvents: 'none',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        }}>
          {data.direction && (
            <span style={{
              fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: data.directionColor,
              background: data.bg, borderRadius: radius.xs, padding: '1px 6px',
              whiteSpace: 'nowrap', lineHeight: 1.4,
            }}>
              {data.direction}
            </span>
          )}
          {data.delay && (
            <span style={{
              fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium, color: data.subtleColor,
              background: data.bg, borderRadius: radius.xs, padding: '1px 6px',
              whiteSpace: 'nowrap', lineHeight: 1.4,
            }}>
              {data.delay}
            </span>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

const NODE_TYPES = { causal: CausalNode };
const EDGE_TYPES = { causal: CausalEdge };

export function CausalChain({ chain, c, isDark }) {
  if (!chain?.nodes?.length) return <p style={{ fontSize: typography.fontSize.md, color: c.textSubtle }}>No causal chain available.</p>;

  const GAP_X = 210, NODE_H = 100, GAP_Y = 20;

  const inDegree = {};
  chain.nodes.forEach((n) => { inDegree[n.id] = 0; });
  chain.edges.forEach((e) => { inDegree[e.target] = (inDegree[e.target] ?? 0) + 1; });

  const col = {};
  const queue = chain.nodes.filter((n) => inDegree[n.id] === 0).map((n) => ({ id: n.id, c: 0 }));
  queue.forEach(({ id, c }) => { col[id] = c; });
  let qi = 0;
  while (qi < queue.length) {
    const { id, c } = queue[qi++];
    chain.edges.filter((e) => e.source === id).forEach((e) => {
      const next = Math.max(col[e.target] ?? 0, c + 1);
      col[e.target] = next;
      queue.push({ id: e.target, c: next });
    });
  }

  const colGroups = {};
  chain.nodes.forEach((n) => {
    const colIdx = col[n.id] ?? 0;
    if (!colGroups[colIdx]) colGroups[colIdx] = [];
    colGroups[colIdx].push(n.id);
  });

  // Barycenter sort — order nodes in each column by avg row-index of their neighbours
  // in the previous column to minimise edge crossings.
  const rowIdx = {}; // id → row within its column (assigned iteratively)
  const sortedCols = Object.keys(colGroups).map(Number).sort((a, b) => a - b);
  sortedCols.forEach((colIdx) => {
    const ids = colGroups[colIdx];
    if (colIdx === 0) {
      ids.forEach((id, i) => { rowIdx[id] = i; });
      return;
    }
    // Score = mean row-index of predecessors in previous column
    const scores = ids.map((id) => {
      const preds = chain.edges.filter((e) => e.target === id).map((e) => rowIdx[e.source] ?? 0);
      return preds.length ? preds.reduce((s, v) => s + v, 0) / preds.length : 0;
    });
    ids.sort((a, b) => scores[ids.indexOf(a)] - scores[ids.indexOf(b)]);
    ids.forEach((id, i) => { rowIdx[id] = i; });
    colGroups[colIdx] = ids;
  });

  const pos = {};
  sortedCols.forEach((colIdx) => {
    const ids = colGroups[colIdx];
    const totalH = ids.length * NODE_H + (ids.length - 1) * GAP_Y;
    ids.forEach((id, i) => {
      pos[id] = { x: colIdx * GAP_X, y: i * (NODE_H + GAP_Y) - totalH / 2 + NODE_H / 2 };
    });
  });

  const flowNodes = chain.nodes.map((n) => ({
    id: n.id, type: 'causal',
    position: pos[n.id] ?? { x: 0, y: 0 },
    data: { label: n.label, nodeType: n.type, value: n.value, isDark, textPrimary: c.textPrimary, textSubtle: c.textSubtle },
  }));

  const edgeLabelBg = isDark ? c.bgMuted : c.bgSubtle;
  // Count edges per source→target pair to offset curvature so parallel edges don't overlap
  const pairCount = {};
  const pairIdx = {};
  chain.edges.forEach((e) => {
    const key = `${e.source}→${e.target}`;
    pairCount[key] = (pairCount[key] ?? 0) + 1;
  });
  chain.edges.forEach((e) => {
    const key = `${e.source}→${e.target}`;
    pairIdx[key] = (pairIdx[key] ?? -1) + 1;
  });
  const pairSeen = {};
  const flowEdges = chain.edges.map((e) => {
    const key = `${e.source}→${e.target}`;
    const idx = pairSeen[key] = (pairSeen[key] ?? -1) + 1;
    const total = pairCount[key] ?? 1;
    // spread curvature: 0 for single, alternating offsets for multiple
    const curvature = total === 1 ? 0.25 : 0.15 + (idx - (total - 1) / 2) * 0.25;
    return {
      id: e.id, source: e.source, target: e.target,
      type: 'causal', animated: true,
      data: {
        direction: e.direction,
        delay: e.label,
        curvature,
        bg: edgeLabelBg,
        borderColor: isDark ? 'rgba(148,163,184,0.6)' : 'rgba(71,85,105,0.45)',
        directionColor: c.textPrimary,
        subtleColor: c.textSubtle,
      },
    };
  });

  const tallest = Object.values(colGroups).reduce((m, ids) => Math.max(m, ids.length), 0);

  return (
    <div style={{ height: Math.max(240, tallest * (NODE_H + GAP_Y) + 40), width: '100%', borderRadius: radius.lg, overflow: 'hidden', border: `1px solid ${c.border}`, background: c.bg }}>
      <ReactFlow
        nodes={flowNodes} edges={flowEdges}
        nodeTypes={NODE_TYPES} edgeTypes={EDGE_TYPES}
        fitView fitViewOptions={{ padding: 0.25 }}
        nodesDraggable={false} nodesConnectable={false}
        panOnDrag={true} panOnScroll={false}
        zoomOnScroll={true} zoomOnPinch={true} zoomOnDoubleClick={false}
        minZoom={0.1} maxZoom={2}
        elementsSelectable={false}
        colorMode={isDark ? 'dark' : 'light'}
        proOptions={{ hideAttribution: true }}
      >
        <Background color={c.border} gap={24} />
        <Controls showInteractive={false} position="bottom-right" />
      </ReactFlow>
    </div>
  );
}

